import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../hooks';
import { useField } from '@unform/core';
import { ProducaoService } from '../services/api/Producao/ProducaoService';

type TAutoCompleteOption = {
  idconf: string;
  idproducao: string;
  label: string;
  meta: number;
  vladicional: number;
  vlproducao: number;
}

type TConfDados = {
  idproducao: string;
  meta: number;
  vladicional: number;
  vlproducao: number;
}
interface IAutoCompleteConfProdutividadeProps {
  isExternalLoading?: boolean;
  uuidproducao?: (novoTextoselect: string | undefined) => void;
  onConfSelecionado?: (dados: TConfDados | undefined) => void;
}

export const AutoCompleteConfProdutividade: React.FC<IAutoCompleteConfProdutividadeProps> = ({
  isExternalLoading = false,
  uuidproducao,
  onConfSelecionado,
}) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField('idconf');
  const { debounce } = useDebounce();

  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ProducaoService.getAlllistConf('', busca)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) return;
          setOpcoes(result.data.map(p => ({
            idconf: p.idconf,
            idproducao: p.idproducao,
            label: p.descricao,
            meta: p.meta,
            vladicional: p.vladicional,
            vlproducao: p.vlproducao,
          })));
        });
    });
  }, [busca]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;
    return opcoes.find(o => o.idconf === selectedId) ?? null;
  }, [selectedId, opcoes]);

  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'
      disablePortal
      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading}
      value={autoCompleteSelectedOption}
      onInputChange={(_, newValue) => setBusca(newValue)}
      onChange={(_, newValue) => {
        setSelectedId(newValue?.idconf);
        uuidproducao?.(newValue?.idconf);
        setBusca('');
        clearError();

        if (newValue) {
          onConfSelecionado?.({
            idproducao: newValue.idproducao,
            meta: newValue.meta,
            vladicional: newValue.vladicional,
            vlproducao: newValue.vlproducao,
          });
        } else {
          onConfSelecionado?.(undefined);
        }
      }}
      popupIcon={isLoading ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label="Produção"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};